import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import FormDialog from '../../common/FormDialog';
import { ledgerAPI } from '../../../services/api';
import {
  AccountPicker,
  CurrencyPicker,
  CostCentrePicker,
  CustomerPicker,
  DepartmentPicker,
  FiscalPeriodPicker,
  LocationPicker,
  ProductPicker,
  PurchaseInvoicePicker,
  SalesInvoicePicker,
  SupplierPicker,
  useLedgerOptions,
} from '../LedgerPickers';

// The ledger rejects manually created OPENING/CLOSING entries (they come from
// fiscal workflows), so only these types are offered for new entries. When
// editing, the caller passes allowedTypes including the entry's current type.
const CREATABLE_ENTRY_TYPES = ['GENERAL', 'ADJUSTMENT', 'RECLASSIFICATION'];

const ALL_ENTRY_TYPES = ['GENERAL', 'ADJUSTMENT', 'RECLASSIFICATION', 'OPENING', 'CLOSING', 'REVERSAL'];

// Must match SourceModule enum in accounting-apis (general-ledger journal-entries).
const SOURCE_MODULES = [
  'MANUAL',
  'INVOICE',
  'PAYMENT',
  'PURCHASE_INVOICE',
  'SALES_INVOICE',
  'SALES_PAYMENT',
  'PURCHASE_PAYMENT',
  'PAYROLL',
  'INVENTORY',
  'DEPRECIATION',
  'ACCRUAL',
  'BANK_RECONCILIATION',
  'ACCOUNT_SETUP',
  'CREDIT_DEBIT_NOTE',
  'TAX_RETURN',
  'FX_VALUATION',
  'FISCAL_CLOSE',
  'EXPENSE_CLAIM',
  'EXPENSE_REIMBURSEMENT',
  'REVENUE_RECOGNITION',
  'COST_ALLOCATION',
  'INTERCOMPANY_INVOICE',
  'INTERCOMPANY_PAYMENT',
  'ELIMINATION',
  'ASSET_ACQUISITION',
  'ASSET_REVALUATION',
  'ASSET_DISPOSAL',
  'CASH_RECEIPT',
  'CASH_PAYMENT',
  'RECURRING_JOURNAL',
];

export const emptyJournalLine = (lineNumber) => ({
  line_number: lineNumber,
  account_id: '',
  debit_amount: 0,
  credit_amount: 0,
  description: '',
  reference: '',
  department_id: '',
  location_id: '',
  cost_center_id: '',
  customer_id: '',
  supplier_id: '',
  product_id: '',
  foreign_currency_id: '',
  foreign_debit: '',
  foreign_credit: '',
  exchange_rate: '',
});

const num = (v) => Number(v) || 0;

// Number inputs everywhere in this form: no spinner arrows, and the mouse
// wheel must scroll the page, not silently change whatever amount happens
// to be under the cursor.
const noSpinnerSx = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
};
const stopWheelChange = (e) => e.target.blur();

// Spreadsheet-style flow for plain text/number inputs: Enter and ArrowDown
// move to the next field, ArrowUp to the previous one — this also doubles as
// the fix for number inputs' native ArrowUp/Down (increment/decrement),
// which we don't want firing here either. Left/Right are deliberately left
// alone: hijacking them would break normal in-field cursor movement.
const focusAdjacentInput = (current, direction) => {
  const root = current.closest('[role="dialog"]') || document;
  const focusables = Array.from(root.querySelectorAll('input:not([disabled]):not([type="hidden"])')).filter(
    (el) => el.offsetParent !== null
  );
  const idx = focusables.indexOf(current);
  if (idx === -1) return;
  const target = focusables[idx + direction];
  if (target) {
    target.focus();
    if (target.select) target.select();
  }
};
const handleGridKeyDown = (e) => {
  if (e.key === 'Enter' || e.key === 'ArrowDown') {
    e.preventDefault();
    focusAdjacentInput(e.currentTarget, 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    focusAdjacentInput(e.currentTarget, -1);
  }
};

// Debit/Credit show comma-grouped amounts (7,500,000) once you leave the
// field; while it's focused it shows the plain digits so typing/editing
// isn't fighting live-inserted commas and cursor jumps.
const formatAmount = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(v);
};
const parseAmountInput = (raw) => raw.replace(/,/g, '');

// Optional per-line dimension/FX fields — QuickBooks-style "add a detail"
// pattern: nothing shows until you pick it from the "+ Add" menu (or it
// already has a value, e.g. when editing), instead of a wall of ten fields
// on every single line.
const EXTRA_FIELD_DEFS = [
  { key: 'reference', label: 'Reference' },
  { key: 'department_id', label: 'Department' },
  { key: 'location_id', label: 'Location' },
  { key: 'cost_center_id', label: 'Cost centre' },
  { key: 'customer_id', label: 'Customer' },
  { key: 'supplier_id', label: 'Supplier' },
  { key: 'product_id', label: 'Product' },
  { key: 'foreign_currency_id', label: 'Foreign currency' },
  { key: 'exchange_rate', label: 'Exchange rate' },
  { key: 'foreign_debit', label: 'Foreign debit' },
  { key: 'foreign_credit', label: 'Foreign credit' },
];
const EXTRA_FIELD_KEYS = EXTRA_FIELD_DEFS.map((f) => f.key);
const hasFieldValue = (line, key) =>
  ['exchange_rate', 'foreign_debit', 'foreign_credit'].includes(key) ? num(line[key]) > 0 : Boolean(line[key]);

// First period whose date range covers the entry date, or null if none does
// (e.g. date falls outside every defined period) — the backend falls back
// to its own resolution in that case.
const findPeriodForDate = (periods, dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr).getTime();
  if (Number.isNaN(d)) return null;
  return (
    periods.find((p) => {
      if (!p.start_date || !p.end_date) return false;
      const start = new Date(p.start_date).getTime();
      const end = new Date(p.end_date).getTime();
      return d >= start && d <= end;
    }) || null
  );
};

const JournalFormDialog = ({ open, onClose, title, values, onChange, onSubmit, saving, allowedTypes, isEdit = false, disableBackdropClose = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Which Debit/Credit cell is currently being edited — that one shows raw
  // digits, every other one shows the comma-formatted amount.
  const [focusedAmount, setFocusedAmount] = useState(null); // { index, key } | null

  const entryTypes = allowedTypes?.length ? allowedTypes : CREATABLE_ENTRY_TYPES;

  // Same 'lookup-periods' cache key Journals.js's useLedgerLookups uses, so
  // this shares the fetch instead of duplicating it.
  const { options: periodsRaw } = useLedgerOptions('lookup-periods', () =>
    ledgerAPI.getFiscalPeriods({ limit: 100 }).then((res) => {
      const payload = res?.data?.data ?? res?.data ?? {};
      return Array.isArray(payload) ? payload : payload.items ?? [];
    })
  );

  // Fiscal period follows the entry date by default — only while creating;
  // an existing entry's period is fixed (the field is disabled below).
  React.useEffect(() => {
    if (isEdit) return;
    const match = findPeriodForDate(periodsRaw, values.entry_date);
    if (match) {
      const id = match.period_id ?? match.id;
      if (String(id) !== String(values.period_id || '')) onChange('period_id', id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.entry_date, periodsRaw, isEdit]);

  const lines = values.lines && values.lines.length ? values.lines : [emptyJournalLine(1)];

  const setHeader = (key) => (e) => onChange(key, e.target.value);
  const setHeaderId = (key) => (id) => onChange(key, id);

  // Which extra fields are showing per line: anything with a value already
  // (e.g. loaded from an existing draft) plus whatever was explicitly added
  // via the "+ Add" menu this session.
  const [manualExtras, setManualExtras] = useState({});
  const [addMenu, setAddMenu] = useState(null); // { index, anchorEl } | null

  const activeFieldsFor = (line, index) => {
    const manual = manualExtras[index];
    return EXTRA_FIELD_KEYS.filter((k) => (manual && manual.has(k)) || hasFieldValue(line, k));
  };

  const addExtraField = (index, key) => {
    setManualExtras((prev) => {
      const next = { ...prev };
      next[index] = new Set(next[index] || []);
      next[index].add(key);
      return next;
    });
  };

  const removeExtraField = (index, key) => {
    setLine(index, key, '');
    setManualExtras((prev) => {
      const next = { ...prev };
      const set = new Set(next[index] || []);
      set.delete(key);
      next[index] = set;
      return next;
    });
  };

  const setLine = (index, key, value) => {
    let next = lines.map((line, i) => (i === index ? { ...line, [key]: value } : line));
    // Double-entry automation (OAS pattern): debit and credit are mutually
    // exclusive; entering one clears the other. If the next line is still
    // empty, mirror the amount to the opposite side and copy the description
    // so a two-line entry balances with one keystroke.
    if ((key === 'debit_amount' && num(value) > 0) || (key === 'credit_amount' && num(value) > 0)) {
      const opposite = key === 'debit_amount' ? 'credit_amount' : 'debit_amount';
      next = next.map((line, i) => (i === index ? { ...line, [opposite]: 0 } : line));
      const following = next[index + 1];
      if (following && !following.account_id && num(following.debit_amount) === 0 && num(following.credit_amount) === 0) {
        next = next.map((line, i) =>
          i === index + 1
            ? {
                ...line,
                [key === 'debit_amount' ? 'credit_amount' : 'debit_amount']: num(value),
                description: line.description || next[index].description || '',
              }
            : line
        );
      }
    }
    // Auto-grow: once the bottom-most line has both an account and an
    // amount, silently append a fresh blank line below it so fast entry
    // keeps pace without a manual "Add line" click after every row.
    const lastIndex = next.length - 1;
    const last = next[lastIndex];
    const lastFilled = Boolean(last?.account_id) && (num(last.debit_amount) > 0 || num(last.credit_amount) > 0);
    if (index === lastIndex && lastFilled) {
      next = [...next, emptyJournalLine(next.length + 1)];
    }
    onChange('lines', next);
  };

  const setLineEvent = (index, key) => (e) => setLine(index, key, e.target.value);
  const setLineId = (index, key) => (id) => setLine(index, key, id);

  // One compact, removable input per active extra field — small "x" tops
  // each one so a field added by mistake (or no longer needed) collapses
  // back into the "+ Add" menu instead of sitting there empty.
  const renderExtraField = (line, i, key) => {
    const def = EXTRA_FIELD_DEFS.find((f) => f.key === key);
    const field = (() => {
      switch (key) {
        case 'reference':
          return <TextField fullWidth size="small" label={def.label} value={line.reference || ''} onChange={setLineEvent(i, 'reference')} onKeyDown={handleGridKeyDown} />;
        case 'department_id':
          return <DepartmentPicker value={line.department_id || ''} onChange={setLineId(i, 'department_id')} />;
        case 'location_id':
          return <LocationPicker value={line.location_id || ''} onChange={setLineId(i, 'location_id')} />;
        case 'cost_center_id':
          return <CostCentrePicker value={line.cost_center_id || ''} onChange={setLineId(i, 'cost_center_id')} />;
        case 'customer_id':
          return <CustomerPicker value={line.customer_id || ''} onChange={setLineId(i, 'customer_id')} />;
        case 'supplier_id':
          return <SupplierPicker value={line.supplier_id || ''} onChange={setLineId(i, 'supplier_id')} />;
        case 'product_id':
          return <ProductPicker value={line.product_id || ''} onChange={setLineId(i, 'product_id')} />;
        case 'foreign_currency_id':
          return <CurrencyPicker value={line.foreign_currency_id || ''} onChange={setLineId(i, 'foreign_currency_id')} />;
        case 'exchange_rate':
          return (
            <TextField
              fullWidth size="small" type="number" label={def.label}
              value={line.exchange_rate || ''} onChange={setLineEvent(i, 'exchange_rate')}
              onWheel={stopWheelChange} onKeyDown={handleGridKeyDown}
              inputProps={{ min: 0, step: 'any' }} sx={noSpinnerSx}
            />
          );
        case 'foreign_debit':
          return (
            <TextField
              fullWidth size="small" type="number" label={def.label}
              value={line.foreign_debit || ''} onChange={setLineEvent(i, 'foreign_debit')}
              onWheel={stopWheelChange} onKeyDown={handleGridKeyDown}
              inputProps={{ min: 0, step: 'any' }} sx={noSpinnerSx}
            />
          );
        case 'foreign_credit':
          return (
            <TextField
              fullWidth size="small" type="number" label={def.label}
              value={line.foreign_credit || ''} onChange={setLineEvent(i, 'foreign_credit')}
              onWheel={stopWheelChange} onKeyDown={handleGridKeyDown}
              inputProps={{ min: 0, step: 'any' }} sx={noSpinnerSx}
            />
          );
        default:
          return null;
      }
    })();
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 170 }}>
        <Box sx={{ flex: 1 }}>{field}</Box>
        <IconButton size="small" onClick={() => removeExtraField(i, key)} title={`Remove ${def.label}`} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    );
  };

  const addLine = () => onChange('lines', [...lines, emptyJournalLine(lines.length + 1)]);

  const removeLine = (index) => {
    if (lines.length <= 2) return;
    onChange(
      'lines',
      lines.filter((_, i) => i !== index).map((line, i) => ({ ...line, line_number: i + 1 }))
    );
    setManualExtras((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, set]) => {
        const v = Number(k);
        if (v < index) next[v] = set;
        else if (v > index) next[v - 1] = set;
      });
      return next;
    });
  };

  const totalDebits = lines.reduce((sum, l) => sum + num(l.debit_amount), 0);
  const totalCredits = lines.reduce((sum, l) => sum + num(l.credit_amount), 0);
  const balanced = Math.abs(totalDebits - totalCredits) < 0.01 && totalDebits > 0;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={title}
      onSubmit={onSubmit}
      saving={saving}
      isMobile={isMobile}
      disabled={!balanced}
      maxWidth="lg"
      disableBackdropClose={disableBackdropClose}
    >
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Basic information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            select
            label="Entry Type"
            value={values.entry_type || 'GENERAL'}
            onChange={setHeader('entry_type')}
            SelectProps={{ native: true }}
            required
          >
            {entryTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Entry Date"
            type="date"
            value={values.entry_date || ''}
            onChange={setHeader('entry_date')}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Reference"
            value={values.reference || ''}
            onChange={setHeader('reference')}
            onKeyDown={handleGridKeyDown}
            placeholder="Optional"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FiscalPeriodPicker
            value={values.period_id || ''}
            onChange={setHeaderId('period_id')}
            disabled={isEdit}
            helper={isEdit ? 'Fixed on the existing entry' : 'Auto-resolved from entry date'}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Description"
            value={values.description || ''}
            onChange={setHeader('description')}
            required
            helperText="Minimum 2 characters"
          />
        </Grid>
      </Grid>

      <Box mt={1}>
        <Button size="small" onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? 'Hide source linkage' : 'Link to source document (optional)'}
        </Button>
        <Collapse in={showAdvanced}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Source module"
                value={values.source_module || ''}
                onChange={(e) => {
                  // Switching module invalidates whatever id was picked
                  // under the old one — never leave a stale mismatched id.
                  onChange('source_module', e.target.value);
                  onChange('source_id', '');
                }}
                SelectProps={{ native: true }}
              >
                <option value="">None (manual entry)</option>
                {SOURCE_MODULES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {values.source_module === 'SALES_INVOICE' ? (
                <SalesInvoicePicker value={values.source_id || ''} onChange={setHeaderId('source_id')} />
              ) : values.source_module === 'PURCHASE_INVOICE' ? (
                <PurchaseInvoicePicker value={values.source_id || ''} onChange={setHeaderId('source_id')} />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Source ID"
                  value={values.source_id || ''}
                  onChange={setHeader('source_id')}
                  onWheel={stopWheelChange}
                  onKeyDown={handleGridKeyDown}
                  disabled={!values.source_module}
                  placeholder={values.source_module ? 'Numeric ID — no picker for this module yet' : 'Pick a module first'}
                  sx={noSpinnerSx}
                />
              )}
            </Grid>
          </Grid>
        </Collapse>
      </Box>

      <Box
        mt={3}
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        sx={{
          position: 'sticky', top: 0, zIndex: 1, py: 1,
          bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Line items ({lines.length}, minimum 2, must balance)
        </Typography>
        {/* Always-visible running totals — the same check repeats at the
            bottom of a potentially long line table, which scrolls out of
            view; this keeps the balance status in sight while typing. */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            Dr {totalDebits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cr {totalCredits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
          {balanced ? (
            <Chip label="Balanced" color="success" size="small" variant="outlined" />
          ) : (
            <Chip
              label={`Out by ${Math.abs(totalDebits - totalCredits).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              color="error"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          A new blank line appears automatically once the last one has an account and an amount.
        </Typography>
      </Box>
      <TableContainer sx={{ mt: 1 }}>
        <Table size="small" sx={{ minWidth: isMobile ? 640 : 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right" sx={{ bgcolor: 'success.50', fontWeight: 700 }}>Debit</TableCell>
              <TableCell align="right" sx={{ bgcolor: 'error.50', fontWeight: 700 }}>Credit</TableCell>
              <TableCell>Details</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, i) => (
              <TableRow key={i}>
                <TableCell>{line.line_number}</TableCell>
                <TableCell>
                  <AccountPicker
                    value={line.account_id}
                    onChange={(id) => setLineId(i, 'account_id')(id)}
                    sx={{ minWidth: 260 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={line.description || ''}
                    onChange={setLineEvent(i, 'description')}
                    onKeyDown={handleGridKeyDown}
                    sx={{ minWidth: 180 }}
                    placeholder="Line narration"
                  />
                </TableCell>
                <TableCell align="right" sx={{ bgcolor: 'success.50' }}>
                  <TextField
                    size="small"
                    type="text"
                    inputMode="decimal"
                    value={
                      focusedAmount?.index === i && focusedAmount?.key === 'debit_amount'
                        ? String(line.debit_amount ?? '')
                        : formatAmount(line.debit_amount)
                    }
                    onChange={(e) => {
                      const raw = parseAmountInput(e.target.value);
                      if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
                      setLine(i, 'debit_amount', raw);
                    }}
                    onFocus={(e) => { setFocusedAmount({ index: i, key: 'debit_amount' }); e.target.select(); }}
                    onBlur={() => setFocusedAmount(null)}
                    onKeyDown={handleGridKeyDown}
                    sx={{ width: 170, bgcolor: 'background.paper' }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ bgcolor: 'error.50' }}>
                  <TextField
                    size="small"
                    type="text"
                    inputMode="decimal"
                    value={
                      focusedAmount?.index === i && focusedAmount?.key === 'credit_amount'
                        ? String(line.credit_amount ?? '')
                        : formatAmount(line.credit_amount)
                    }
                    onChange={(e) => {
                      const raw = parseAmountInput(e.target.value);
                      if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
                      setLine(i, 'credit_amount', raw);
                    }}
                    onFocus={(e) => { setFocusedAmount({ index: i, key: 'credit_amount' }); e.target.select(); }}
                    onBlur={() => setFocusedAmount(null)}
                    onKeyDown={handleGridKeyDown}
                    sx={{ width: 170, bgcolor: 'background.paper' }}
                  />
                </TableCell>
                {/* Active extras + "Add detail" trail off horizontally in
                    this same row instead of pushing a row open below it —
                    the TableContainer scrolls sideways once it's wider than
                    the dialog. */}
                {activeFieldsFor(line, i).map((key) => (
                  <TableCell key={key} sx={{ p: 1 }}>{renderExtraField(line, i, key)}</TableCell>
                ))}
                <TableCell sx={{ p: 1 }}>
                  <IconButton size="small" onClick={(e) => setAddMenu({ index: i, anchorEl: e.currentTarget })} title="Add detail">
                    <AddIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="error" disabled={lines.length <= 2} onClick={() => removeLine(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3} align="right">
                <strong>Totals</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{totalDebits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{totalCredits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              </TableCell>
              <TableCell colSpan={2}>
                {balanced ? (
                  <Chip label="Balanced" color="success" size="small" variant="outlined" />
                ) : (
                  <Chip
                    label={`Out by ${Math.abs(totalDebits - totalCredits).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Menu anchorEl={addMenu?.anchorEl} open={Boolean(addMenu)} onClose={() => setAddMenu(null)}>
        {addMenu &&
          EXTRA_FIELD_DEFS.filter((f) => !activeFieldsFor(lines[addMenu.index], addMenu.index).includes(f.key)).map((f) => (
            <MenuItem
              key={f.key}
              onClick={() => {
                addExtraField(addMenu.index, f.key);
                setAddMenu(null);
              }}
            >
              {f.label}
            </MenuItem>
          ))}
      </Menu>
      <Box mt={1}>
        <Button size="small" startIcon={<AddIcon />} onClick={addLine}>
          Add line
        </Button>
      </Box>
      {!balanced && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          Debits must equal credits and be greater than zero before saving.
        </Typography>
      )}
    </FormDialog>
  );
};

export default JournalFormDialog;
export { ALL_ENTRY_TYPES };
